/**
 * Users API functions
 */
import { ApiKeyCreateResponse, ApiKeyInfo, User, UserMe } from '@/types';
import { apiClient } from './client';

export interface UpdateUserRequest {
  first_name?: string;
  last_name?: string;
  email?: string;
}

export interface UpdateUserProfileRequest {
  first_name?: string;
  last_name?: string;
}

export interface CreateApiKeyRequest {
  name: string;
}

export const usersApi = {
  /**
   * Get current user profile
   */
  async getProfile(): Promise<UserMe> {
    return apiClient.get<UserMe>('/users/me');
  },

  /**
   * Update current user profile
   */
  async updateProfile(data: UpdateUserProfileRequest): Promise<UserMe> {
    return apiClient.put<UserMe>('/users/me', data);
  },

  /**
   * Get current user's API keys metadata
   */
  async getApiKeys(): Promise<ApiKeyInfo[]> {
    return apiClient.get<ApiKeyInfo[]>('/users/me/api-keys');
  },

  /**
   * Create a new API key and return plaintext key once
   */
  async createApiKey(data: CreateApiKeyRequest): Promise<ApiKeyCreateResponse> {
    return apiClient.post<ApiKeyCreateResponse>('/users/me/api-keys', data);
  },

  /**
   * Delete (deactivate) API key
   */
  async deleteApiKey(apiKeyId: string): Promise<void> {
    return apiClient.delete<void>(`/users/me/api-keys/${apiKeyId}`);
  },

  /**
   * Get user by ID (admin only)
   */
  async getById(userId: string): Promise<User> {
    return apiClient.get<User>(`/users/${userId}`);
  },

  /**
   * Update user by ID (admin only)
   */
  async updateById(userId: string, data: UpdateUserRequest): Promise<User> {
    return apiClient.patch<User>(`/users/${userId}`, data);
  },

  /**
   * Delete user by ID (admin only)
   */
  async deleteById(userId: string): Promise<void> {
    return apiClient.delete(`/users/${userId}`);
  },

  /**
   * Activate user (admin only)
   */
  async activate(userId: string): Promise<User> {
    return apiClient.post<User>(`/users/${userId}/activate`);
  },

  /**
   * Deactivate user (admin only)
   */
  async deactivate(userId: string): Promise<User> {
    return apiClient.post<User>(`/users/${userId}/deactivate`);
  },
};
